import { Kicker } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';

// Route title block: an optional eyebrow — the shared mono `Kicker` behind the
// one pulsing live dot — a 500-weight tracking-tight title, a muted description,
// and a right-aligned actions slot, all over a hairline rule (`_patterns.scss`).
// The dot paints `--status-live`, so `eyebrow` is for a view whose content is
// live-streamed; a static label belongs in `desc`.
export function PageHeader(props: {
  title: string;
  desc?: string;
  /**
   * A slot UNDER the description, inside the lead column — for a fact line the
   * route wants read with the title rather than an action or a state word (the
   * session header's token/worth line). Kept distinct from `actions`, which is
   * right-aligned and holds controls.
   */
  below?: JSX.Element;
  eyebrow?: string; // when set, shows the pulsing LIVE eyebrow above the title
  actions?: JSX.Element;
  // Focus target: some routes move keyboard focus to the title after a mutation
  // (e.g. AccountKeys after a revoke removes the focused card). When set, the h1
  // becomes programmatically focusable (tabindex=-1).
  titleRef?: (el: HTMLHeadingElement) => void;
}): JSX.Element {
  return (
    <header class="page-header">
      <div class="page-header__lead">
        <Show when={props.eyebrow}>
          <p class="page-header__eyebrow">
            <span class="page-header__eyebrow-dot" aria-hidden="true" />
            <Kicker as="span">{props.eyebrow}</Kicker>
          </p>
        </Show>
        <h1
          class="page-header__title"
          ref={(el) => props.titleRef?.(el)}
          tabindex={props.titleRef ? -1 : undefined}
        >
          {props.title}
        </h1>
        <Show when={props.desc}>
          <p class="page-header__desc">{props.desc}</p>
        </Show>
        <Show when={props.below}>
          <div class="page-header__below">{props.below}</div>
        </Show>
      </div>
      <Show when={props.actions}>
        <div class="page-header__actions">{props.actions}</div>
      </Show>
    </header>
  );
}
